import {
  Font,
  Icon,
  RadioButton,
} from "@moneyforward/mfx_biz_react_components";
import { color } from "@/styles/theme";
import { css } from "@emotion/react";
import { SelectRowContainer } from "@/components/atoms/SelectRowContainer";
import { SelectRowItemCell } from "@/components/atoms/SelectRowItemCell";
import { BankServices, Plan, Service } from "@/generated/graphql_billing";
import React, { MouseEventHandler, useState } from "react";
import {
  filterActiveOtherServices,
  sortServicesByDisplayOrder,
  shortenServiceName,
  ServiceAnnotationConfig,
} from "@/libs/billing/service";
import { PlanServiceCell } from "@/components/molecules/PlanServiceCell";

type Prop = JSX.IntrinsicElements["div"] & {
  plan: Plan;
  services: BankServices["services"];
  selectedValue: string;
  inContract?: boolean;
  serviceAnnotationOrderMap: Map<string, ServiceAnnotationConfig>;
  onDetailClick?: MouseEventHandler<HTMLDivElement>;
};

export const SelectPlanRowItem = (props: Prop) => {
  const {
    plan,
    services,
    onClick,
    onDetailClick,
    selectedValue,
    inContract,
    serviceAnnotationOrderMap,
    ...divProps
  } = props;

  const NUMBER_OF_SERVICES = services.length || 0;
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);
  const otherServicesOfPlan = sortServicesByDisplayOrder(
    filterActiveOtherServices(plan.services)
  );
  const displayPlanRowItemCell = (
    services: Service[],
    targetService: Service,
    displayIndex: number
  ) => {
    const rowService = services.find(
      (service) => service.id === targetService.id
    );
    return (
      <SelectRowItemCell
        key={`SelectRowItemCell-${displayIndex}`}
        rightBorderDisplay={displayIndex === NUMBER_OF_SERVICES ? true : false}
        externalStyleClass={
          displayIndex === NUMBER_OF_SERVICES ? externalStyleClass : null
        }
        backgroundColor={displayIndex % 2 === 0 ? color.gray100 : color.white}
        onClick={onClick}
      >
        <PlanServiceCell service={rowService} />
      </SelectRowItemCell>
    );
  };

  /**
   * Create component for description area of other services
   * @param services - Other services
   * @param annoServiceCodes - Service codes to be annotated
   */
  const planRowItemCellForOtherServices = (otherServices: Service[]) => {
    // Requirements:
    // 1. If there are other services, display them.
    //    If there are no other services, display "-"
    // 2. Add '※#N' to the end of the service name
    //    '※' means Annotation in japanese
    const customFontSizeStyle = css({
      fontSize: "1.0rem",
    });
    return otherServices.length > 0 ? (
      otherServices.map((service, index) => {
        const isAnnotated = serviceAnnotationOrderMap.has(service.code);
        let serviceNameString = shortenServiceName(service.name, service.code);
        if (isAnnotated) {
          const annotatedCfg = serviceAnnotationOrderMap.get(service.code);
          serviceNameString = `${shortenServiceName(
            service.name,
            service.code
          )}※${annotatedCfg.order}`;
        }

        return (
          <div
            key={index}
            css={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
              alignSelf: "flex-start",
              justifySelf: "flex-start",
            }}
          >
            <Font css={customFontSizeStyle}>{serviceNameString}</Font>
          </div>
        );
      })
    ) : (
      <Font css={customFontSizeStyle}>-</Font>
    );
  };

  return (
    <SelectRowContainer
      minWidth={960}
      minHeight={80}
      gridTemplateColumns={`2.28fr 1.3fr ${
        services && services.map((_) => "1.3fr").join(" ")
      } 0.01fr 1.95fr 0.01fr 0.55fr`}
      onMouseOver={(_) => setIsMouseOver(true)}
      onMouseLeave={(_) => setIsMouseOver(false)}
      {...divProps}
    >
      <SelectRowItemCell
        css={[
          isMouseOver
            ? {
                backgroundColor: color.gray100,
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
              }
            : null,
          { justifyContent: "flex-start" },
          inContract && {
            padding: 0,
          },
        ]}
        rightBorderDisplay
        onClick={onClick}
      >
        <div
          css={
            inContract
              ? { padding: 0, display: "inherit", height: "inherit" }
              : { paddingLeft: 16, display: "inherit" }
          }
        >
          {inContract ? (
            <div
              css={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: 32,
                height: "100%",
                padding: 8,
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                backgroundColor: color.gray600,
              }}
            >
              <Font
                variant="s"
                css={{ color: color.white, textAlign: "center" }}
              >
                契約中
              </Font>
            </div>
          ) : (
            <RadioButton
              id={`${plan.id}-radio`}
              name="plan"
              value={plan.id}
              onChange={() => {}}
              checked={plan.id === selectedValue}
            />
          )}
          <Font
            css={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              marginLeft: 8,
              marginRight: 8,
            }}
            variant="lb"
          >
            {plan.name}
          </Font>
        </div>
      </SelectRowItemCell>

      <SelectRowItemCell
        css={isMouseOver ? { backgroundColor: color.gray100 } : null}
        rightBorderDisplay
        onClick={onClick}
      >
        <Font variant="xlb">
          {plan.priceIncludingTax === 0
            ? "無料"
            : plan.priceIncludingTax.toLocaleString()}
        </Font>
        {plan.priceIncludingTax > 0 && <Font variant="sb">円/月</Font>}
      </SelectRowItemCell>
      {services.map((service, index) =>
        displayPlanRowItemCell(plan?.services, service, index + 1)
      )}
      <SelectRowItemCell
        css={{ width: 5 }}
        rightBorderDisplay
        externalStyleClass={externalStyleClass}
        onClick={onClick}
      />
      <SelectRowItemCell
        css={{
          paddingLeft: 5,
          paddingRight: 5,
          display: "flex",
          flexDirection: "column",
        }}
        rightBorderDisplay
        externalStyleClass={externalStyleClass}
        onClick={onClick}
      >
        {planRowItemCellForOtherServices(otherServicesOfPlan)}
      </SelectRowItemCell>
      <SelectRowItemCell
        css={{ width: 5 }}
        rightBorderDisplay
        externalStyleClass={externalStyleClass}
        onClick={onClick}
      />
      <SelectRowItemCell
        className="plan-detail"
        css={{ cursor: "pointer" }}
        onClick={onDetailClick}
      >
        <Font css={{ color: color.gray600 }} variant="s">
          詳細
        </Font>
      </SelectRowItemCell>
    </SelectRowContainer>
  );
};

const externalStyleClass = css({
  paddingTop: 0,
  paddingBottom: 0,
});
